import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { includeOptions, outputOptions } from "../data/constants";

/**
 * Small inline "?" help tip affordance â€” mirrors the .mca-tip/.mca-tip__bubble
 * pattern used across the converted overlays (e.g. CollectionForm).
 */
const HelpTip = ({ text }) => (
  <span className="mca-tip">
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="var(--mca-muted)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 4 1.5c0 1.5-2 2-2 3" />
      <path d="M12 17h.01" />
    </svg>
    <span className="mca-tip__bubble">{text}</span>
  </span>
);

export const APIRequestFields = ({
  isCustomID = false,
  requestType = "amazon",
}) => {
  const _domainList = useSelector((state) => state.zipCodeReducer.domainList);
  const _asinDetails = useSelector((state) => state.asinReducer.asinDetails);
  const _zipCodesList = useSelector(
    (state) => state.zipCodeReducer.zipCodesList
  );
  const _officeworksStoreList = useSelector(
    (state) => state.zipCodeReducer.officeworksStoreList
  );

  const isOfficeworks = requestType === "officeworks";
  const isRepco = requestType === "repco";

  // â”€â”€ Controlled field state (seeded from _asinDetails in edit mode) â”€â”€
  const [domain, setDomain] = useState("");
  const [asin, setAsin] = useState("");
  const [sku, setSku] = useState("");
  const [storeId, setStoreId] = useState("");
  const [customerPostcode, setCustomerPostcode] = useState("");
  const [output, setOutput] = useState(outputOptions[0]?.value ?? "");
  const [includeRawHtml, setIncludeRawHtml] = useState(
    String(includeOptions[0]?.value)
  );
  const [customId, setCustomId] = useState("");

  // Default the Amazon domain select: in edit mode use the record's own domain
  // (so its postcode stays valid under the per-domain filter), else the first.
  useEffect(() => {
    if (!_domainList || _domainList.length === 0) return;
    const editDomainId = _asinDetails?.domain?.id ?? _asinDetails?.domain;
    if (
      editDomainId != null &&
      _domainList.some((d) => String(d.id) === String(editDomainId))
    ) {
      setDomain(String(editDomainId));
    } else {
      setDomain(String(_domainList[0].id));
    }
  }, [_domainList, _asinDetails]);

  // Seed all fields from the record being edited. Runs again when the async
  // option lists arrive so the controlled values match real options.
  useEffect(() => {
    if (!_asinDetails) return;

    setAsin(_asinDetails?.asin ?? "");
    setSku(_asinDetails?.asin ?? "");
    setCustomId(_asinDetails?.custom_id ?? "");

    // Officeworks store was previously read from custom_id in the legacy code.
    if (_asinDetails?.custom_id != null) setStoreId(String(_asinDetails.custom_id));

    if (_asinDetails?.customer_postcode != null) {
      const matched = _zipCodesList?.find(
        (item) => item?.id == _asinDetails?.customer_postcode
      );
      setCustomerPostcode(matched ? String(matched.id) : "");
    }

    if (_asinDetails?.output != null) {
      const matchedOut = outputOptions.find(
        (option) => option.value === _asinDetails?.output
      );
      if (matchedOut) setOutput(matchedOut.value);
    }

    if (_asinDetails?.include_raw_html != null) {
      const matchedInc = includeOptions.find(
        (option) => option.value === _asinDetails?.include_raw_html
      );
      if (matchedInc) setIncludeRawHtml(String(matchedInc.value));
    }
  }, [_asinDetails, _zipCodesList]);

  if (isRepco) {
    return (
      <div className="form-stack">
        <div className="mca-field">
          <label htmlFor="api-repco-code" className="mca-label">
            Product Code <span className="req">*</span>
            <HelpTip text="Repco product code (e.g. A8334587)" />
          </label>
          <input
            id="api-repco-code"
            type="text"
            className="mca-input mca-cell-mono"
            required
            name="sku"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
          />
        </div>

        <div className="mca-field">
          <label htmlFor="api-repco-postcode" className="mca-label">
            Customer Postcode <span className="req">*</span>
            <HelpTip text="Australian customer postcode — drives the delivery store and freight" />
          </label>
          <select
            id="api-repco-postcode"
            className="mca-select"
            required
            name="customer_postcode"
            value={customerPostcode}
            onChange={(e) => setCustomerPostcode(e.target.value)}
          >
            <option value="">Select postcode...</option>
            {(_zipCodesList || []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.zipcode}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (isOfficeworks) {
    return (
      <div className="form-stack">
        <div className="row-2">
          <div className="mca-field">
            <label htmlFor="api-sku" className="mca-label">
              SKU <span className="req">*</span>
              <HelpTip text="Officeworks Product SKU" />
            </label>
            <input
              id="api-sku"
              type="text"
              className="mca-input mca-cell-mono"
              required
              name="sku"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </div>

          <div className="mca-field">
            <label htmlFor="api-store" className="mca-label">
              Store
              <HelpTip text="Officeworks Store" />
            </label>
            <select
              id="api-store"
              className="mca-select"
              name="store_id"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
            >
              <option value="">Select store...</option>
              {(_officeworksStoreList || []).map((store) => (
                <option key={store.store_id} value={store.store_id}>
                  {store.store_name} ({store.state})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mca-field">
          <label htmlFor="api-ow-postcode" className="mca-label">
            Customer Postcode <span className="req">*</span>
            <HelpTip text="Australian Customer Postcode" />
          </label>
          <select
            id="api-ow-postcode"
            className="mca-select"
            required
            name="customer_postcode"
            value={customerPostcode}
            onChange={(e) => setCustomerPostcode(e.target.value)}
          >
            <option value="">Select postcode...</option>
            {(_zipCodesList || []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.zipcode}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Postcodes belong to a domain — only offer the ones for the selected domain
  // (e.g. AU postcodes for amazon.com.au). Match on the domain name carried on
  // each zipcode's nested `domain` object (with an id fallback).
  const selectedDomainName = (_domainList || []).find(
    (d) => String(d.id) === String(domain)
  )?.domain;
  const amazonZipCodes = (_zipCodesList || []).filter((zc) => {
    if (!selectedDomainName) return true;
    const zd = zc?.domain;
    if (zd == null) return false;
    if (typeof zd === "object") {
      return zd.domain === selectedDomainName || String(zd.id) === String(domain);
    }
    return String(zd) === String(domain);
  });

  // â”€â”€ Amazon fields (default) â”€â”€
  return (
    <div className="form-stack">
      <div className="row-2">
        <div className="mca-field">
          <label htmlFor="api-domain" className="mca-label">
            Amazon Domain
            <HelpTip text="Amazon Domain" />
          </label>
          <select
            id="api-domain"
            className="mca-select"
            name="domain"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              setCustomerPostcode("");
            }}
          >
            {(_domainList || []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.domain}
              </option>
            ))}
          </select>
        </div>

        <div className="mca-field">
          <label htmlFor="api-asin" className="mca-label">
            ASIN <span className="req">*</span>
            <HelpTip text="ASIN" />
          </label>
          <input
            id="api-asin"
            type="text"
            className="mca-input mca-cell-mono"
            required
            name="asin"
            value={asin}
            onChange={(e) => setAsin(e.target.value)}
          />
        </div>
      </div>

      <div className="mca-field">
        <label htmlFor="api-postcode" className="mca-label">
          Customer Postcode/Zipcode <span className="req">*</span>
          <HelpTip text="Customer Postcode/Zipcode" />
        </label>
        <select
          id="api-postcode"
          className="mca-select"
          required
          name="customer_postcode"
          value={customerPostcode}
          onChange={(e) => setCustomerPostcode(e.target.value)}
        >
          <option value="">Select postcode...</option>
          {amazonZipCodes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.zipcode}
            </option>
          ))}
        </select>
      </div>

      <div className="row-2">
        <div className="mca-field">
          <label htmlFor="api-output" className="mca-label">
            Output <span className="req">*</span>
            <HelpTip text="Output" />
          </label>
          <select
            id="api-output"
            className="mca-select"
            required
            name="output"
            value={output}
            onChange={(e) => setOutput(e.target.value)}
          >
            {outputOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mca-field">
          <label htmlFor="api-include" className="mca-label">
            Include Raw HTML
            <HelpTip text="Include Raw HTML" />
          </label>
          <select
            id="api-include"
            className="mca-select"
            name="include_raw_html"
            value={includeRawHtml}
            onChange={(e) => setIncludeRawHtml(e.target.value)}
          >
            {includeOptions.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isCustomID && (
        <div className="mca-field">
          <label htmlFor="api-custom-id" className="mca-label">
            Custom ID
            <HelpTip text="Custom ID" />
          </label>
          <input
            id="api-custom-id"
            type="text"
            className="mca-input"
            name="custom_id"
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
          />
        </div>
      )}
    </div>
  );
};